import { Injectable } from '@angular/core';
import { ModalService } from 'app/core/services/modal.service';
import {
  Account,
  AccountService,
  ChannelName,
  ConfigService,
  CookieService,
  CustomerSubscription,
  ServiceName,
  SubscriptionsService,
} from 'ngx-web-api';
import { SmsModalContext } from './sms-modal.context';
import { Observable, noop } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { SessionStorageService } from 'app/core/services/session-storage-service';

@Injectable({ providedIn: 'root' })
export class SmsModalService {
  account: Account = null;
  hasSmsOffersCoupon: boolean = false;
  private loadingModal = false;

  constructor(
    private cookieService: CookieService,
    private modalService: ModalService,
    private accountService: AccountService,
    private storeConfig: ConfigService,
    private http: HttpClient,
    private sessionStorageService: SessionStorageService,
    private router: Router
  ) {
    this.accountService.account$.subscribe(user => {
      this.account = user.copy();
    });
    this.storeConfig.storeConfig$.subscribe(store => {
      this.hasSmsOffersCoupon = store.hasSmsOffersCoupon;
    });
  }

  initSmsModal() {
    if (this.loadingModal || !this.shouldOpenModal()) {
      return;
    }
    // The modal is downloaded only when it has to be shown, to keep it out of the initial bundle
    this.loadingModal = true;
    import('./sms-modal.component')
      .then(({ SmsModalComponent }) => {
        if (this.shouldOpenModal()) {
          this.modalService.openModal(SmsModalComponent, new SmsModalContext().getConfig()).subscribe(noop);
        }
      }, noop)
      .finally(() => (this.loadingModal = false));
  }

  fetchCoupon(): Observable<any> {
    //here will make the http call to get the coupon info
    return this.http.get('/ws/integrated/v1/ordering/config/smsOffersCoupon');
  }

  /**
   * This method is resposible desiding if the modal should open or not.
   * Any cases that affect this should be added here
   *
   * case 1 : User has the cookie set on his browser
   * case 2 : User is already subscribed.
   * case 3 : Session started from a table/tent QR code (/welcome).
   *
   * @returns if we should show the modal or not.
   */
  private shouldOpenModal(): boolean {
    // QR dine-in flow Guard
    if (this.sessionStorageService.isQrLandingSource() || this.router.url.startsWith('/welcome')) {
      return false;
    }

    // Cookie Guard
    const cookie_value = this.cookieService.getSmsAcknowledgementCookie();
    if (cookie_value === '1') {
      return false;
    }

    if (!this.hasSmsOffersCoupon) {
      return false;
    }

    // User Has already subscribed
    if (!this.account) return true;
    for (const sub of this.account.subscriptions) {
      if (
        sub.channelName === ChannelName.SMS &&
        sub.services.findIndex(subService => subService.serviceName == ServiceName.OFFERS && subService.subscribed) !== -1
      ) {
        return false;
      }
    }

    return true;
  }

  addSmsChannel(subscriptions: CustomerSubscription[]): CustomerSubscription[] {
    const subscriptionToAdd = new CustomerSubscription();
    subscriptionToAdd.channelName = ChannelName.SMS;
    subscriptions.push(subscriptionToAdd);
    return subscriptions;
  }

  addEmailChannel(subscriptions: CustomerSubscription[]): CustomerSubscription[] {
    const emailSubscription = new CustomerSubscription();
    emailSubscription.channelName = ChannelName.EMAIL;
    emailSubscription.services = [];
    subscriptions.push(emailSubscription);
    return subscriptions;
  }

  addEmailOffersService(subscriptions: CustomerSubscription[]): CustomerSubscription[] {
    const serviceToAdd = new SubscriptionsService();
    serviceToAdd.serviceName = ServiceName.OFFERS;
    serviceToAdd.subscribed = true;

    for (let i = 0; i < subscriptions.length; i++) {
      if (subscriptions[i].channelName === ChannelName.EMAIL) {
        if (!subscriptions[i].services) {
          subscriptions[i].services = [];
        }
        subscriptions[i].services.push(serviceToAdd);
      }
    }
    return subscriptions;
  }

  addEmailOffers(subscriptions: CustomerSubscription[]): CustomerSubscription[] {
    const emailChannelExists = subscriptions.find(channel => channel.channelName === ChannelName.EMAIL);
    const emailOffersExists = emailChannelExists?.services.find(
      (service: SubscriptionsService) => service.serviceName === ServiceName.OFFERS
    );

    if (!emailChannelExists) {
      const serviceToAdd = new SubscriptionsService();
      serviceToAdd.serviceName = ServiceName.OFFERS;
      serviceToAdd.subscribed = true;

      const channelToAdd = new CustomerSubscription();
      channelToAdd.channelName = ChannelName.EMAIL;
      channelToAdd.services = [serviceToAdd];

      subscriptions.push(channelToAdd);
      return subscriptions;
    } else if (!emailOffersExists) {
      const updatedSubscriptions = this.addEmailOffersService(subscriptions);
      return updatedSubscriptions;
    } else {
      subscriptions.forEach(channel => {
        if (channel.channelName === ChannelName.EMAIL && channel.services) {
          channel.services.forEach(service => {
            if (service.serviceName === ServiceName.OFFERS) {
              service.subscribed = true;
            }
          });
        }
      });

      return subscriptions;
    }
  }

  addOffersService(subscriptions: CustomerSubscription[]): CustomerSubscription[] {
    //Preper the service
    const serviceToAdd = new SubscriptionsService();
    serviceToAdd.serviceName = ServiceName.OFFERS;
    serviceToAdd.subscribed = true;

    //Add the service to the channel
    for (let i = 0; i < subscriptions.length; i++) {
      if (subscriptions[i].channelName === ChannelName.SMS) {
        const offersIndex = subscriptions[i].services?.findIndex(service => service.serviceName == ServiceName.OFFERS);
        if (offersIndex == -1) {
          subscriptions[i].services.push(serviceToAdd);
          return subscriptions;
        } else if (offersIndex === undefined) {
          subscriptions[i].services = [serviceToAdd];
        } else {
          subscriptions[i].services[offersIndex].subscribed = true;
          return subscriptions;
        }
      }
    }
    return subscriptions;
  }
}
